import { ArrowLeft, Bell } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import PortalNotificationComposer from "src/components/portal-notifications/PortalNotificationComposer";
import PortalNotificationPreview from "src/components/portal-notifications/PortalNotificationPreview";
import { actorFromAuth } from "src/lib/activityService";
import { APP_ENV } from "src/lib/appEnv";
import {
  createPortalNotification,
  getPortalNotificationDraftFieldErrors,
  getPortalNotificationFieldErrors,
  publishPortalNotification,
  resolveTargetCompanyNames,
  sanitizeLocalDatetime,
  summarizePortalNotificationErrors,
  updatePortalNotification,
} from "src/lib/portalNotificationService";
import { canReadService, canWriteService, ServiceDomain } from "src/lib/roles";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import type { PortalNotificationFormState } from "src/types/portalNotifications";
import { Constants } from "../constant";

/** Fresh defaults each time the composer opens — not baked at module load. */
const createInitialForm = (): PortalNotificationFormState => ({
  env: APP_ENV,
  category: "NEWS",
  headline: "",
  body: "",
  ctaLabel: "",
  ctaUrl: "",
  startAtLocal: "",
  endAtLocal: "",
  targeting: "ALL_COMPANIES",
  targetCompanies: [],
});

interface ComposeLocationState {
  prefill?: Partial<PortalNotificationFormState>;
  /**
   * When set, the composer is editing an existing draft row — Save updates
   * it in place; Publish updates then publishes the same id.
   */
  notificationId?: string;
}

const PortalNotificationsComposePanel: React.FC = () => {
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as ComposeLocationState | null;
  const prefill = navState?.prefill;
  const [notificationId, setNotificationId] = useState<string | null>(
    navState?.notificationId ?? null,
  );

  const canWrite =
    userProfile != null &&
    canWriteService(userProfile.role, ServiceDomain.PortalNotifications);

  const [form, setForm] = useState<PortalNotificationFormState>(() => {
    const merged: PortalNotificationFormState = {
      ...createInitialForm(),
      ...(prefill ?? {}),
      // Env is fixed to this deployment — never inherit it from a prefill.
      env: APP_ENV,
    };
    // Zero / epoch window from the API must stay empty (same as create).
    merged.startAtLocal = sanitizeLocalDatetime(merged.startAtLocal);
    merged.endAtLocal = sanitizeLocalDatetime(merged.endAtLocal);
    return merged;
  });
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Draft/edit prefill only has company ids — resolve portal names on open.
  useEffect(() => {
    const companies = form.targetCompanies;
    if (!companies.some((c) => !c.name || c.name === c.id)) return;

    let cancelled = false;
    void resolveTargetCompanyNames(form.env, companies).then((resolved) => {
      if (cancelled) return;
      setForm((prev) => ({
        ...prev,
        targetCompanies: prev.targetCompanies.map((c) => {
          const match = resolved.find((r) => r.id === c.id);
          return match && match.name !== c.id ? match : c;
        }),
      }));
    });

    return () => {
      cancelled = true;
    };
    // Resolve once when the composer opens with a prefill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [validationMode, setValidationMode] = useState<"draft" | "publish">(
    "draft",
  );
  const fieldErrors = useMemo(() => {
    if (
      validationMode === "draft" &&
      !form.headline.trim() &&
      !form.body.trim()
    ) {
      return {};
    }
    return validationMode === "publish"
      ? getPortalNotificationFieldErrors(form)
      : getPortalNotificationDraftFieldErrors(form);
  }, [form, validationMode]);

  const save = async (): Promise<string | null> => {
    const actor = actorFromAuth(user, userProfile);
    if (notificationId) {
      const updated = await updatePortalNotification(
        form.env,
        notificationId,
        form,
        actor,
      );
      return updated.id;
    }
    const created = await createPortalNotification(form.env, form, actor);
    setNotificationId(created.id);
    return created.id;
  };

  const handleSaveDraft = async () => {
    setError(null);
    setSuccess(null);
    setValidationMode("draft");
    const draftErrors = summarizePortalNotificationErrors(
      getPortalNotificationDraftFieldErrors(form),
    );
    if (draftErrors) {
      setError(draftErrors);
      return;
    }
    setSaving(true);
    const updating = Boolean(notificationId);
    try {
      await save();
      setSuccess(updating ? "Draft updated." : "Draft saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save draft.");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setError(null);
    setSuccess(null);
    setValidationMode("publish");
    const publishErrors = summarizePortalNotificationErrors(
      getPortalNotificationFieldErrors(form),
    );
    if (publishErrors) {
      setError(publishErrors);
      return;
    }
    const actor = actorFromAuth(user, userProfile);
    setPublishing(true);
    try {
      const id = await save();
      if (!id) throw new Error("Could not save notification.");
      await publishPortalNotification(form.env, id, form.headline, actor);
      setSuccess("Published. Redirecting…");
      window.setTimeout(
        () => navigate(Constants.PORTAL_NOTIFICATIONS_ROUTE),
        900,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Publish failed.");
    } finally {
      setPublishing(false);
    }
  };

  if (
    !userProfile ||
    !canReadService(userProfile.role, ServiceDomain.PortalNotifications)
  ) {
    return (
      <div className="py-6 sm:py-8">
        <h1 className="mb-3 text-2xl font-bold sm:mb-4 sm:text-4xl">
          Access Denied
        </h1>
        <p className="text-sm text-gray-400 sm:text-base">
          You don&apos;t have permission to view portal notifications.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl py-2 sm:py-4">
      <header className="mb-6 sm:mb-8">
        <button
          type="button"
          onClick={() => navigate(Constants.PORTAL_NOTIFICATIONS_ROUTE)}
          className="mb-3 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-extrabold uppercase tracking-wide text-[#a9acb3] hover:text-[#ffffff]"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Back to portal
          notifications
        </button>
        <h1 className="mb-2 flex items-center gap-2 text-2xl font-bold sm:gap-3 sm:text-4xl">
          <Bell className="h-7 w-7 shrink-0 text-white sm:h-8 sm:w-8" />
          {notificationId
            ? "Edit draft"
            : prefill
              ? "Duplicate notification"
              : "New notification"}
        </h1>
        <p className="max-w-2xl text-sm text-[#d7dadf] sm:text-base">
          Compose a login pop-up for the Client Portal. Save it as a draft or
          publish it into the scheduled window.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-start">
        <PortalNotificationComposer
          form={form}
          onChange={setForm}
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublish}
          saving={saving}
          publishing={publishing}
          editingDraft={Boolean(notificationId)}
          canWrite={canWrite}
          fieldErrors={fieldErrors}
          error={error}
          success={success}
        />
        <PortalNotificationPreview form={form} />
      </div>
    </div>
  );
};

export default PortalNotificationsComposePanel;
